"use client";

import React from "react";
import { Mail, MapPin, Phone, Instagram, Twitter, Linkedin, Send, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Message Received</h3>
        <p className="text-muted-foreground mb-8 max-w-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted hover:text-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem className="relative">
              <FormControl>
                <Input {...field} placeholder="Jane Doe" className="peer h-14 bg-background border-input text-foreground placeholder:text-transparent focus-visible:ring-secondary focus-visible:border-secondary pt-5 pb-1 transition-all" />
              </FormControl>
              <FormLabel className="absolute left-3 top-1.5 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-secondary pointer-events-none">
                Full Name
              </FormLabel>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="relative">
              <FormControl>
                <Input type="email" {...field} placeholder="jane@example.com" className="peer h-14 bg-background border-input text-foreground placeholder:text-transparent focus-visible:ring-secondary focus-visible:border-secondary pt-5 pb-1 transition-all" />
              </FormControl>
              <FormLabel className="absolute left-3 top-1.5 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-secondary pointer-events-none">
                Email Address
              </FormLabel>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="relative">
              <FormControl>
                <Input type="tel" {...field} placeholder="+1 555-000-0000" className="peer h-14 bg-background border-input text-foreground placeholder:text-transparent focus-visible:ring-secondary focus-visible:border-secondary pt-5 pb-1 transition-all" />
              </FormControl>
              <FormLabel className="absolute left-3 top-1.5 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-secondary pointer-events-none">
                Phone Number (Optional)
              </FormLabel>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem className="relative">
              <FormControl>
                <Input {...field} placeholder="e.g., Volunteer Inquiry" className="peer h-14 bg-background border-input text-foreground placeholder:text-transparent focus-visible:ring-secondary focus-visible:border-secondary pt-5 pb-1 transition-all" />
              </FormControl>
              <FormLabel className="absolute left-3 top-1.5 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-secondary pointer-events-none">
                Inquiry Type
              </FormLabel>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please enter your message"
    }} render={({
      field
    }) => <FormItem className="relative">
            <FormControl>
              <Textarea {...field} placeholder="How can we support you today?" className="peer min-h-[160px] bg-background border-input text-foreground placeholder:text-transparent focus-visible:ring-secondary focus-visible:border-secondary pt-7 pb-3 resize-none transition-all" />
            </FormControl>
            <FormLabel className="absolute left-3 top-2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-5 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs peer-focus:text-secondary pointer-events-none">
              Your Message
            </FormLabel>
            <FormMessage className="text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-default font-medium shadow-lg shadow-secondary/20 hover:shadow-secondary/40 transition-all duration-300 group bg-primary text-primary-foreground">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            Send Message
            <Send className="ml-2 size-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </>}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224516" id="get-in-touch" ref={sectionRef} className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24 relative z-10">
        
        {}
        <div className={`flex flex-col justify-center transition-all duration-1000 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 tracking-tight text-primary">
              Join the <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-chart-4">
                Trust Community
              </span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-md font-default">
              Whether you are seeking support, looking to volunteer, or interested in partnering with us, we are here to listen and walk alongside you.
            </p>
          </div>

          <div className="space-y-10">
            <div className="flex items-start gap-5 group">
              <div className="size-12 rounded-xl bg-muted border border-border flex items-center justify-center text-secondary group-hover:scale-110 group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 shrink-0">
                <Mail className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-default font-semibold text-muted-foreground uppercase tracking-wider mb-1">Email Us</h3>
                <Link to="mailto:hello@thetrustfoundation.org" className="text-xl font-default font-medium text-foreground hover:text-secondary transition-colors">
                  hello@thetrustfoundation.org
                </Link>
              </div>
            </div>

            <div className="flex items-start gap-5 group">
              <div className="size-12 rounded-xl bg-muted border border-border flex items-center justify-center text-secondary group-hover:scale-110 group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 shrink-0">
                <Phone className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-default font-semibold text-muted-foreground uppercase tracking-wider mb-1">Call Us</h3>
                <Link to="tel:+18005550199" className="text-xl font-default font-medium text-foreground hover:text-secondary transition-colors">
                  +1 (800) 555-0199
                </Link>
              </div>
            </div>

            <div className="flex items-start gap-5 group">
              <div className="size-12 rounded-xl bg-muted border border-border flex items-center justify-center text-secondary group-hover:scale-110 group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 shrink-0">
                <MapPin className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-default font-semibold text-muted-foreground uppercase tracking-wider mb-1">Our Location</h3>
                <p className="text-xl font-default font-medium text-foreground max-w-[280px]">
                  789 Empowerment Way, Community Center, ST 12345
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-10 border-t border-border">
            <h3 className="text-sm font-default font-semibold text-muted-foreground uppercase tracking-wider mb-6">Follow Our Impact</h3>
            <div className="flex gap-4">
              {[{
              icon: Instagram,
              label: "Instagram",
              href: "#"
            }, {
              icon: Twitter,
              label: "Twitter",
              href: "#"
            }, {
              icon: Linkedin,
              label: "LinkedIn",
              href: "#"
            }].map((social, i) => <Link key={i} to={social.href} className="size-12 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-secondary-foreground hover:border-secondary transition-all duration-300 hover:-translate-y-1" aria-label={social.label}>
                  <social.icon className="size-5" />
                </Link>)}
            </div>
          </div>
        </div>

        {}
        <div className={`transition-all duration-1000 delay-200 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <FormProvider formKey="get_in_touch_form_support_us" formId="get_in_touch_form_support_us" sectionName="Get In Touch" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            pattern: "phone"
          },
          message: {
            required: true
          }
        }} submitText="Send Message" successMessage="Thank you for reaching out to The Trust Foundation. We have received your message and will respond with care shortly." className="bg-card/80 backdrop-blur-sm p-8 md:p-10 rounded-3xl border border-border shadow-xl shadow-primary/5">
            <div className="mb-8">
              <h3 className="text-2xl font-serif font-semibold text-primary mb-2">Send a Message</h3>
              <p className="text-muted-foreground text-sm font-default">Fill out the form below and our team will be in touch.</p>
            </div>
            <ContactForm />
          </FormProvider>
        </div>

      </div>
    </section>;
}